import { apiClient } from './api-client';

// Portal Configuration types
export interface PortalTheme {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  headerColor: string;
  footerColor: string;
}

export interface PortalConfig {
  id: string;
  tenantId: string;
  name?: string;
  subdomain?: string;
  customDomain?: string;
  logoUrl?: string;
  faviconUrl?: string;
  theme: PortalTheme;
  enableDarkMode: boolean;
  enableSearch: boolean;
  enableContactForm: boolean;
  enableChat: boolean;
  enableTicketSubmission: boolean;
  enableTicketTracking: boolean;
  welcomeMessage?: string;
  footerText?: string;
  customCss?: string;
  customJs?: string;
  metaTitle?: string;
  metaDescription?: string;
  googleAnalyticsId?: string;
  languages: string[];
  defaultLanguage: string;
  metadata?: Record<string, any>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SocialMediaLinks {
  twitter?: string;
  linkedin?: string;
  facebook?: string;
  instagram?: string;
  youtube?: string;
}

export interface CreatePortalConfigDto {
  name?: string;
  subdomain?: string;
  customDomain?: string;
  logoUrl?: string;
  faviconUrl?: string;
  theme?: Partial<PortalTheme>;
  enableDarkMode?: boolean;
  enableSearch?: boolean;
  enableContactForm?: boolean;
  enableChat?: boolean;
  enableTicketSubmission?: boolean;
  enableTicketTracking?: boolean;
  welcomeMessage?: string;
  footerText?: string;
  customCss?: string;
  customJs?: string;
  metaTitle?: string;
  metaDescription?: string;
  googleAnalyticsId?: string;
  languages?: string[];
  defaultLanguage?: string;
  metadata?: Record<string, any>;
}

export interface UpdatePortalConfigDto extends Partial<CreatePortalConfigDto> {}

export interface CssVariables {
  [key: string]: string;
}

export const portalConfigService = {
  /**
   * Get portal configuration
   */
  async get(): Promise<PortalConfig | null> {
    try {
      const response = await apiClient.get('/portal/admin/config');
      return response.data;
    } catch (error: unknown) {
      if ((error as { response?: { status?: number } }).response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Create portal configuration
   */
  async create(dto: CreatePortalConfigDto): Promise<PortalConfig> {
    const response = await apiClient.post('/portal/admin/config', dto);
    return response.data;
  },

  /**
   * Update portal configuration
   */
  async update(dto: UpdatePortalConfigDto): Promise<PortalConfig> {
    const response = await apiClient.put('/portal/admin/config', dto);
    return response.data;
  },

  /**
   * Delete portal configuration
   */
  async delete(): Promise<void> {
    await apiClient.delete('/portal/admin/config');
  },

  /**
   * Get CSS variables for theming
   */
  async getCssVariables(): Promise<CssVariables> {
    const response = await apiClient.get('/portal/admin/css-variables');
    return response.data;
  },

  /**
   * Generate CSS from portal config
   */
  generateCss(config: PortalConfig): string {
    const { theme } = config;
    return `
:root {
  --portal-primary: ${theme.primaryColor};
  --portal-secondary: ${theme.secondaryColor};
  --portal-accent: ${theme.accentColor};
  --portal-background: ${theme.backgroundColor};
  --portal-text: ${theme.textColor};
  --portal-header: ${theme.headerColor};
  --portal-footer: ${theme.footerColor};
}

${config.customCss || ''}
    `.trim();
  },

  /**
   * Get portal preview URL
   */
  getPreviewUrl(_config: PortalConfig): string {
    return '/portal-preview';
  },

  /**
   * Validate subdomain availability
   */
  async validateSubdomain(subdomain: string): Promise<{ available: boolean; message?: string }> {
    try {
      const response = await apiClient.get(`/portal/admin/validate-subdomain?subdomain=${encodeURIComponent(subdomain)}`);
      return response.data;
    } catch {
      return { available: false, message: 'Unable to validate subdomain' };
    }
  },
};

export default portalConfigService;
